import { useEffect, useRef, useState } from "react";

const REPORTS = [
  {
    id: "melanoma",
    tab: "Melanoma Scan Beta",
    tabDate: "NOV 2, 2026",
    tag: "REPORT · NOV 2026",
    title: "MDCE Melanoma Scan Beta —",
    accent: "Expanded Human-Submitted Image Evaluation",
    date: "POSTED · NOVEMBER 2, 2026",
    status: "BETA",
    introLabel: "SCOPE",
    intro: (
      <>
        Internal technical evaluation of{" "}
        <span className="text-white">image intake, body-map registration, and
        change-over-time comparison</span> on volunteer-submitted photographs collected
        after the September 29 first-pass results.
      </>
    ),
    metrics: [
      { pct: 86, k: "Image-Quality", kicker: "USABILITY PASS RATE" },
      { pct: 89, k: "Body-Map", kicker: "REGISTRATION SUCCESS" },
      { pct: 91, k: "Sequential Alignment", kicker: "COMPLETION" },
      { pct: 84, k: "End-to-End Workflow", kicker: "COMPLETION" },
    ],
    panels: [
      {
        label: "NOTES",
        bullets: [
          "Results are from controlled internal review of real-world user captures.",
          "Public dermatology archives (HAM10000, ISIC Archive, TCIA) were used in earlier training phases.",
          "Human-submitted images introduce additional variability in lighting, device, distance, angle, and compression.",
          "Metrics reflect technical processing only and are not clinical performance measures.",
        ],
      },
    ],
    disclaimer:
      "MDCE Melanoma Scan Beta is in beta-stage development. It has not received regulatory clearance and is not intended to diagnose, treat, cure, or prevent any disease. It is not a substitute for professional medical evaluation.",
  },
  {
    id: "wound",
    tab: "Wound Monitoring",
    tabDate: "OCT 12, 2026",
    tag: "REPORT · OCT 2026",
    title: "MDCE Wound Monitoring —",
    accent: "Internal Technical Evaluation",
    date: "PUBLISHED · OCTOBER 12, 2026",
    status: "CONTINUOUS TRAINING",
    introLabel: "OVERVIEW",
    intro: (
      <>
        Internal technical evaluation results for the{" "}
        <span className="text-white">MDCE Wound Monitoring</span> application, covering
        image-quality scoring, wound-boundary segmentation, and longitudinal comparison
        on controlled sequential smartphone captures. These are{" "}
        <span className="text-white">not clinical study results</span>, not independently
        verified clinical endpoints, and not a claim of clinical efficacy, diagnostic
        accuracy, or therapeutic performance.
      </>
    ),
    metrics: [
      {
        pct: 84,
        k: "Image-Quality Scoring",
        kicker: "USABILITY PASS RATE",
        body: "Usability pass rate on controlled sequential captures. Frames below threshold — motion blur, extreme distance, inconsistent lighting — were held out of comparison so the workflow rejects unusable input rather than force a comparison on a poor frame.",
      },
      {
        pct: 87,
        k: "Wound-Boundary Segmentation",
        kicker: "BOUNDARY STABILITY",
        body: "Boundary stability on usable sequences, with reduced drift relative to the prior internal cycle when lighting and distance remained within the usable range. Remains sensitive to partial occlusion and low-contrast edges — active training targets.",
      },
      {
        pct: 91,
        k: "Longitudinal Comparison",
        kicker: "ALIGNMENT COMPLETION",
        body: "Alignment completion rate on multi-frame usable sequences. Sequential smartphone images that clear the quality filter can be organized for change-over-time visual review. Workflow finding, not a clinical finding.",
      },
    ],
    panels: [
      {
        label: "CONTEXT",
        text: (
          <>
            These internal metrics are reported as part of ongoing model training and are
            not positioned against independent clinical benchmarks. The Company has
            previously described the{" "}
            <span className="text-white">chronic wound care market as exceeding $20
            billion annually</span>, projected to surpass{" "}
            <span className="text-white">$30 billion by 2030</span>.
          </>
        ),
      },
      {
        label: "NEXT CYCLE",
        text: (
          <>
            Models remain in continuous training. Subsequent cycles aim to increase the
            share of{" "}
            <span className="text-white">everyday user-captured sequences that clear the
            quality gate</span> and to improve{" "}
            <span className="text-white">segmentation stability on low-contrast edges</span>.
          </>
        ),
      },
    ],
    disclaimer:
      "MDCE Wound Monitoring has not received regulatory clearance. It is not intended to diagnose, treat, cure, or prevent any disease or medical condition. It is not a substitute for professional medical advice, wound care, or evaluation by a qualified healthcare provider. Any concerning change in a wound should be reviewed by a licensed clinician. The figures above are internal technical evaluation metrics only.",
  },
];

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

function useCountUp(target, active, delay) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion()) {
      setVal(target);
      return;
    }
    let raf;
    const duration = 1400;
    const start = performance.now() + delay;
    const tick = (now) => {
      const t = Math.min(Math.max((now - start) / duration, 0), 1);
      setVal(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, delay]);
  return val;
}

function MetricTile({ m, idx, testid }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const delay = (idx - 1) * 140;
  const val = useCountUp(m.pct, inView, delay);
  return (
    <div ref={ref} data-testid={testid} className="glass-panel p-6 lg:p-7 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-36 h-36 bg-[radial-gradient(circle,rgba(160,107,255,0.22),transparent_70%)] blur-2xl pointer-events-none" />
      <div className="relative">
        <div className="font-mono-tech text-[10px] text-[#a06bff] tracking-[0.22em]">METRIC · 0{idx}</div>
        <div className="mt-3 flex items-baseline gap-3">
          <div
            data-testid={`${testid}-value`}
            className="font-display font-bold text-[48px] lg:text-[56px] leading-none text-gradient-violet tabular-nums"
          >
            {val}%
          </div>
          <div className="font-mono-tech text-[10px] text-[#a991d6] tracking-[0.2em] flex-1">{m.kicker}</div>
        </div>
        <div className="mt-4 h-1.5 w-full rounded-full bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.25)] overflow-hidden">
          <div
            data-testid={`${testid}-bar`}
            className="metric-bar-fill h-full bg-gradient-to-r from-[#a06bff] to-[#d6b8ff] shadow-[0_0_10px_rgba(160,107,255,0.6)]"
            style={{ transform: `scaleX(${inView ? m.pct / 100 : 0})`, transitionDelay: `${delay}ms` }}
          />
        </div>
        <h4 className="mt-5 font-display text-white text-lg lg:text-xl leading-tight">{m.k}</h4>
        {m.body && <p className="mt-2 text-[13px] lg:text-[13.5px] text-[#b9add0] leading-relaxed">{m.body}</p>}
      </div>
    </div>
  );
}

function Panel({ label, children, testid }) {
  return (
    <div data-testid={testid} className="glass-panel p-6 lg:p-7">
      <div className="font-mono-tech text-[10px] text-[#a06bff] tracking-[0.22em]">{label}</div>
      <div className="mt-3 text-[#cfc4e6] leading-relaxed text-[14px] lg:text-[14.5px]">{children}</div>
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((n) => (
        <li key={n} className="flex gap-3">
          <span className="mt-[8px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#a06bff] shadow-[0_0_8px_rgba(160,107,255,0.8)]" />
          <span>{n}</span>
        </li>
      ))}
    </ul>
  );
}

function ReportHeader({ r }) {
  return (
    <div className="glass-panel image-frame corner-accents p-6 lg:p-8 lg:col-span-5 flex flex-col justify-between">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono-tech text-[10px] text-[#a06bff] tracking-[0.24em]">{r.tag}</div>
        <div className="font-mono-tech text-[10px] text-[#d6b8ff] tracking-[0.2em] flex items-center gap-2 px-2.5 py-1 rounded-full border border-[rgba(160,107,255,0.35)] bg-[rgba(160,107,255,0.08)]">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a06bff] animate-pulse" />
          {r.status}
        </div>
      </div>
      <h3 data-testid={`report-title-${r.id}`} className="mt-5 font-display font-bold text-2xl lg:text-[32px] leading-[1.1]">
        <span className="text-white">{r.title} </span>
        <span className="text-gradient-violet">{r.accent}</span>
      </h3>
      <p className="mt-4 text-[#a991d6] font-mono-tech text-[10.5px] tracking-[0.22em]">{r.date}</p>
    </div>
  );
}

function Report({ r }) {
  const cols = r.metrics.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "lg:grid-cols-3";
  return (
    <div data-testid={`${r.id}-report`} role="tabpanel" className="report-fade flex flex-col gap-5">
      <div className="grid lg:grid-cols-12 gap-5">
        <ReportHeader r={r} />
        <div className="lg:col-span-7">
          <Panel label={r.introLabel} testid={`${r.id}-intro`}>{r.intro}</Panel>
        </div>
      </div>
      <div className={`grid grid-cols-1 ${cols} gap-5`}>
        {r.metrics.map((m, i) => (
          <MetricTile key={m.k} m={m} idx={i + 1} testid={`${r.id}-metric-${i + 1}`} />
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        {r.panels.map((p) => (
          <Panel key={p.label} label={p.label} testid={`${r.id}-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
            {p.bullets ? <Bullets items={p.bullets} /> : p.text}
          </Panel>
        ))}
        <div
          data-testid={`${r.id}-disclaimer`}
          className={`border-l-2 border-[rgba(160,107,255,0.45)] bg-[rgba(11,6,18,0.55)] px-5 py-4 rounded-r-lg self-start ${r.panels.length > 1 ? "lg:col-span-2" : ""}`}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.22em] text-[#a991d6]">DISCLAIMER</div>
          <p className="mt-2 text-[12.5px] lg:text-[13px] text-[#b9add0] leading-[1.65]">{r.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}

function ReportTabs({ active, onChange }) {
  return (
    <div role="tablist" data-testid="report-tabs" className="grid grid-cols-2 w-full sm:inline-grid sm:w-auto gap-1.5 sm:gap-2 p-1.5 rounded-2xl glass-panel mb-8">
      {REPORTS.map((r, i) => {
        const on = r.id === active;
        return (
          <button
            key={r.id}
            role="tab"
            aria-selected={on}
            data-testid={`report-tab-${r.id}`}
            onClick={() => onChange(r.id)}
            className={`text-left px-3 sm:px-5 py-2.5 rounded-xl transition-[background-color,box-shadow,color] duration-300 ${
              on
                ? "bg-gradient-to-r from-[#7129e6] to-[#a06bff] text-white shadow-[0_10px_30px_-12px_rgba(160,107,255,0.9)]"
                : "text-[#b9add0] hover:bg-[rgba(160,107,255,0.1)] hover:text-white"
            }`}
          >
            <div className="font-display text-[13px] sm:text-[15px] font-semibold leading-tight flex flex-wrap items-center gap-x-2 gap-y-1">
              {r.tab}
              {i === 0 && (
                <span className={`font-mono-tech text-[9px] tracking-[0.18em] px-1.5 py-0.5 rounded ${on ? "bg-white/20" : "bg-[rgba(160,107,255,0.18)] text-[#d6b8ff]"}`}>
                  LATEST
                </span>
              )}
            </div>
            <div className={`font-mono-tech text-[9.5px] tracking-[0.2em] mt-0.5 ${on ? "text-white/80" : "text-[#8d83a6]"}`}>
              {r.tabDate}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function TestingResults() {
  const [active, setActive] = useState(REPORTS[0].id);
  const report = REPORTS.find((r) => r.id === active);
  return (
    <section id="testing-results" data-testid="testing-results-section" className="relative py-20 lg:py-28">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/4 w-[55rem] h-[36rem] bg-[radial-gradient(ellipse_at_center,rgba(138,61,255,0.16),transparent_70%)] blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8 lg:mb-10">
          <div className="max-w-3xl">
            <div className="section-num">03 / TESTING RESULTS</div>
            <h2 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.05]">
              <span className="text-white">Internal </span>
              <span className="text-gradient-violet">Technical Evaluations</span>
            </h2>
            <p className="mt-4 text-[#b9add0] text-[15px] leading-relaxed">
              Technical processing metrics from the Company's ongoing internal testing of
              its MDCE applications. Not clinical performance measures.
            </p>
          </div>
          <div className="font-mono-tech text-[11px] text-[#a06bff] tracking-[0.18em] flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a06bff] animate-pulse" />
            MODELS IN CONTINUOUS TRAINING
          </div>
        </div>
        <ReportTabs active={active} onChange={setActive} />
        <Report key={report.id} r={report} />
      </div>
    </section>
  );
}
